import React, { useContext, useEffect, useRef, useState, useMemo } from 'react';
import { Tag as TagComponent } from 'Atoms';
import { Typography } from 'Tokens';
import {
  VStack,
  Box,
  Skeleton,
  HStack,
  Divider,
  Popover,
  PopoverContent,
  PopoverBody,
  useDisclosure,
  Center,
  PopoverTrigger,
  Portal,
  useOutsideClick,
} from '@chakra-ui/react';
import { SearchInput, Select } from 'Molecules';
import { useTranslation } from 'utils/translation';
import { useDebounce, useCurrentCompanyId } from 'utils/hooks';
import mixpanel from 'mixpanel-browser';
import { TRACKING_EVENTS } from '../../utils/mixpanel';
import {
  useArticles,
  useArticleSearch,
  useArticlesWithTags,
  useKnowledgeBase,
  useTagGroups,
} from './KnowledgeBase.hooks';
import { useSearchParams, useLocation } from 'react-router-dom';
import { GlobalContext } from 'containers/Navigation';
import { components } from 'react-select';
import { CheckIcon } from '@chakra-ui/icons';
import { getModuleName, SideMenuItem } from 'containers/Navigation/pieces';
import { useCurrentCompany } from 'utils/hooks';
import { ArticleAccordion } from '.';


type TagOption = {
  value: string;
  label: string;
  description: string;
};

type GroupedTags = {
  label: string;
  options: TagOption[];
};

const TopicSelector = ({
  selected,
  setSelected,
  groupedTags,
  loading,
}: {
  selected: string;
  setSelected: (val?: string) => void;
  groupedTags: GroupedTags[];
  loading: boolean;
}) => {
  const { t } = useTranslation('common');
  const allTags = groupedTags.map((tagsGroup) => tagsGroup.options).flat();
  const selectedTag = selected ? allTags.find((x) => x.value === selected) : null;

  const formatGroupLabel = (group: { label?: string; options: readonly TagOption[] }) => (
    <Typography variant="detail" color="text.default">
      {group.label}
    </Typography>
  );

  if (loading) return <Skeleton width="300px" isLoaded={!loading} height="32px" />;
  return (
    <Select<TagOption>
      value={selectedTag}
      onChange={(newVal) => setSelected(newVal?.value ?? undefined)}
      options={groupedTags}
      isSearchable={false}
      formatGroupLabel={formatGroupLabel}
      placeholder={t('common:knowledgeBase.select')}
      components={{
        Option: (props) => (
          <HStack
            {...props.innerProps}
            ref={props.innerRef}
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            padding="8px 16px"
            borderRadius="8px"
            cursor="pointer"
            _hover={{
              bg: 'bg.hover',
            }}
            onClick={(e) => props?.innerProps?.onClick?.(e)}
          >
            <VStack alignItems="flex-start">
              <Typography
                variant="bodyStrong"
                color={selected === props.data.value ? 'text.selected' : 'text.default'}
              >
                {props.data.label}
              </Typography>
            </VStack>
            <VStack color="text.selected">
              {selected === props.data.value && <CheckIcon color="inherit" />}
            </VStack>
          </HStack>
        ),
        SingleValue: ({ children, ...props }) => {
          return <components.SingleValue {...props}>{'Topic: ' + children}</components.SingleValue>;
        },
        GroupHeading: (groupProps) => {
          return (
            <components.GroupHeading {...groupProps}>
              <Box padding="8px">{groupProps.children as React.ReactNode}</Box>
            </components.GroupHeading>
          );
        },
        Group: (groupProps) => {
          return (
            <components.Group {...groupProps}>
              <Box>
                {groupProps.children as React.ReactNode}
                <Divider width="auto" color="border.decorative" my="0px" mr="8px" />
              </Box>
            </components.Group>
          );
        },
      }}
      styles={{
        groupHeading: (base) => ({
          ...base,
          paddingInline: '8px',
        }),
        menu: (base) => ({
          ...base,
          width: 'max-content',
          maxWidth: '300px',
          paddingInline: '8px',
          right: 0,
        }),
      }}
    />
  );
};

export type KnowledgeBaseSidebarButtonType = {
  item: SideMenuItem;
  isOpen: boolean;
  isScreenLarge: boolean;
};

export const KnowledgeBaseSidebarButton = ({
  item,
  isOpen,
  isScreenLarge,
}: KnowledgeBaseSidebarButtonType) => {
  const { company } = useCurrentCompany();
  const { pathname } = useLocation();
  const { t } = useTranslation(['common']);
  const isHome = item?.path === '/';
  const isCurrent = () => (isHome ? pathname === `/${company?.id}` : pathname.includes(item.path));
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Accordion state
  const [expandedArticleIndexes, setExpandedArticleIndexes] = useState<number[]>([]);


  // Popover state management
  const { isOpen: isPopoverOpen, onOpen, onClose, onToggle } = useDisclosure();
  const [searchParams, setSearchParams] = useSearchParams();

  const { onOpen: onKnowledgeOpen, onClose: onKnowledgeClose } = useKnowledgeBase();

  // Handle URL parameter for opening knowledge base
  useEffect(() => {
    const withKnowledgeBase = searchParams.get('withKnowledgeBase');
    if (withKnowledgeBase && !isPopoverOpen) {
      onOpen();
    }
  }, [searchParams, isPopoverOpen, onOpen]);

  const handleClose = () => {
    onClose();
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.delete('withKnowledgeBase');
    newSearchParams.delete('slug');
    newSearchParams.delete('article');
    setSearchParams(newSearchParams.toString());
  };

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isPopoverOpen) {
      handleClose();
    } else {
      onOpen();
    }
  };

  // Knowledge Base logic
  const { companyId } = useCurrentCompanyId();
  const [searchValue, setSearchValue] = useState('');
  const [activeTag, setActiveTag] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [selectedTagGroup, setSelectedTagGroup] = useState<string>('all');
  const { helpCenterOpen, setHelpCenterOpen } = useContext(GlobalContext);

  useEffect(() => {
    if (helpCenterOpen) {
      onOpen();
    }
  }, [helpCenterOpen, onOpen]);

  useEffect(() => {
    if (!isScreenLarge && !isOpen) {
      onClose();
    }
  }, [isScreenLarge, isOpen, onClose]);

  const debouncedQuery = useDebounce(searchValue, 1000);

  const { searched, searchIsLoading } = useArticleSearch(debouncedQuery);

  const { articles } = useArticles();
  const { tags, tagsAreLoading } = useArticlesWithTags();
  const { tagGroups, tagGroupsAreLoading } = useTagGroups();

  const tagGroupsWithAll = useMemo(() => [
    { id: 'all', name: t('common:words.all'), description: '' },
    ...tagGroups
  ], [tagGroups, t]);

  const filteredTagGroups = useMemo(() => {
    if (selectedTagGroup === 'all') return tagGroups;

    const found = tagGroups.find((i) => i.id === selectedTagGroup);
    return found ? [found] : [];
  }, [selectedTagGroup, tagGroups]);

  const groupedTags = useMemo(() => filteredTagGroups.map((tagGroup) => {
    return {
      label: tagGroup.name,
      options: tags
        .filter((t) => t.tagGroup?.id === tagGroup.id)
        .map((t) => ({
          value: t.id,
          label: t.name,
          description: t.description,
        })),
    };
  }), [filteredTagGroups, tags]);

  const articlesToShow = useMemo(() => {
    let list = articles;

    if (searchValue !== '') list = searched;
    if (activeTag !== '') list = articles.filter((article) => article.tags.find((i) => i.Tag.id === activeTag));
    if (selectedTagGroup !== 'all') list = articles.filter((article) => article.tags.find((tag) => tag.Tag.tagGroup?.id === selectedTagGroup));

    return [...list].sort((a, b) => Number(a.order) - Number(b.order));
  }, [searchValue, searched, activeTag, articles, selectedTagGroup]);

  const focusedIndex = useMemo(() => {
    if (!isPopoverOpen) return -1; // << only consider slug after popover is open

    const slug = searchParams.get('slug');
    const articleId = searchParams.get('article');
    if (!slug && !articleId) return -1;

    return articlesToShow.findIndex((a) => {
      if (articleId) return a.id === articleId;
      const aSlug = (a as any).slug;
      return aSlug && aSlug === slug;
    });
  }, [isPopoverOpen, searchParams, articlesToShow]);

  // When a valid focusedIndex exists, expand that item
  useEffect(() => {
    if (!isPopoverOpen) return;
    if (focusedIndex < 0) return;

    setExpandedArticleIndexes([focusedIndex]);
  }, [isPopoverOpen, focusedIndex]);

  // Keep Accordion controlled w.r.t user interaction
  const handleAccordionChange = (next: number | number[]) => {
    setExpandedArticleIndexes(Array.isArray(next) ? next : [next]);
  };


  const setSearch = (input: { value: string }) => {
    setLoading(true);
    setSearchValue(input.value);
    setActiveTag('');
    setTimeout(() => setLoading(false), 1200);
  };

  const handleSelectTagGroup = (tagGroupId: string) => {
    setSelectedTagGroup(tagGroupId);
    setSearchValue('');
    setActiveTag('');
  };

  useOutsideClick({
    ref: sidebarRef,
    handler: () => {
      setExpandedArticleIndexes([]);
      if (!isPopoverOpen) return;
      handleClose();
    },
    enabled: isPopoverOpen,
  });

  return (
    <Popover
      placement="right"
      isOpen={isPopoverOpen}
      onClose={handleClose}
      closeOnBlur={false}
      isLazy={true}
    >
      <PopoverTrigger>
        <Box
          color="text.onAccent.disabled"
          width="100%"
          textDecoration="none"
          bg={isCurrent() ? 'bg.onAccent.selected' : 'none'}
          borderRadius="8px"
          cursor="pointer"
          _pressed={{ bg: 'bg.pressed' }}
          _hover={{ bg: 'bg.onAccent.hover', textDecoration: 'none' }}
          onClick={onToggle}
        >
          <HStack
            as="span"
            spacing="8px"
            p="12px"
            width="100%"
            justifyContent="space-between"
            padding="0 12px"
            height="36px"
          >
            <HStack as="span" spacing="8px" width="100%">
              <Center boxSize="20px">
                <item.Icon boxSize="20px" color="text.sidebar" />
              </Center>
              <Typography
                color="text.sidebar"
                variant="bodyStrong"
                transition="all 0.4s"
                noOfLines={1}
                opacity={isOpen ? 1 : 0}
              >
                {getModuleName(t, item.key)}
              </Typography>
            </HStack>
          </HStack>
        </Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          width="680px"
          height="100vh"
          border="1px solid"
          borderColor="border.decorative"
          boxShadow="0px 0px 72px -2px rgba(15, 15, 46, 0.36), 0px 6px 8px -2px rgba(15, 15, 46, 0.06), 0px 0px 0px 1px rgba(15, 15, 46, 0.12)"
          bg="bg.default"
          color="black"
          ref={sidebarRef}
        >
          <PopoverBody padding="0px">
            <VStack spacing="0px" height="100%">
              {/* Header */}
              <Box width="100%" p="16px" borderBottom="1px solid" borderColor="border.decorative">
                <Typography variant="bodyStrong">{t('common:knowledgeBase.title')}</Typography>
              </Box>

              {/* Knowledge Base Content */}
              <VStack
                spacing="0px"
                width="100%"
                height="calc(100vh - 64px)"
                overflowY="auto"
                onClick={(e) => {
                  // Close popover when clicking on links
                  if (['A'].includes((e.target as HTMLElement).tagName)) {
                    handleClose();
                  }
                }}
              >
                <VStack width="100%" spacing="16px" p="16px">
                  <SearchInput
                    search={searchValue}
                    setSearch={(input) => setSearch({ value: input })}
                    placeholder={t('common:knowledgeBase.search')}
                    width="100%"
                    size="md"
                    onFocus={() => {
                      mixpanel.track(TRACKING_EVENTS.KNOWLEDGE_BASE.SEARCH, {
                        companyId,
                        query: searchValue,
                      });
                    }}
                  />

                  <HStack
                    justifyContent="space-between"
                    alignItems="stretch"
                    width="100%"
                    height="36px"
                  >
                    <HStack spacing="6px">
                      {tagGroupsWithAll.map((tagGroup) => {
                        return (
                          <TagComponent
                            key={tagGroup.id}
                            clickable
                            isSelected={selectedTagGroup === tagGroup.id}
                            height="100%"
                            width="auto"
                            cursor={selectedTagGroup === tagGroup.id ? 'default' : 'pointer'}
                            _hover={{
                              bg: 'bg.hover',
                            }}
                            onClick={() => handleSelectTagGroup(tagGroup.id)}
                          >
                            {tagGroup.name}
                          </TagComponent>
                        );
                      })}
                    </HStack>
                    <Box width="200px">
                      <TopicSelector
                        groupedTags={groupedTags ?? []}
                        selected={activeTag ?? ''}
                        setSelected={(val) => setActiveTag(val ?? '')}
                        loading={tagsAreLoading || tagGroupsAreLoading}
                      />
                    </Box>
                  </HStack>
                </VStack>

                <Box width="100%" px="16px" pb="16px">
                  <ArticleAccordion articlesToShow={articlesToShow} isLoading={loading || searchIsLoading} setSearchValue={setSearchValue} expandedArticleIndexes={expandedArticleIndexes} focusedIndex={focusedIndex} handleAccordionChange={handleAccordionChange} />
                </Box>
              </VStack>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
